/**
 * Category composition threshold steps. Shared by the URL parser
 * (which drops any other value) and the threshold slider, so the two can't
 * drift apart.
 */
export const THRESHOLD_STEPS = [5, 10, 25, 50, 100] as const
export type ThresholdStep = (typeof THRESHOLD_STEPS)[number]

/** Applied whenever a category is selected and the buyer hasn't picked one. */
export const DEFAULT_THRESHOLD: ThresholdStep = 10

export const isThresholdStep = (value: unknown): value is ThresholdStep =>
  (THRESHOLD_STEPS as readonly unknown[]).includes(value)

type CategorySelection = {
  categories?: readonly string[] | null
  category?: readonly string[] | null
}

/** True when a top-level category or a subcategory is selected. */
export const hasCategorySelection = (filters: CategorySelection) =>
  (filters.categories?.length ?? 0) > 0 || (filters.category?.length ?? 0) > 0

/**
 * The threshold that actually narrows results: only applies alongside a
 * category selection, otherwise null. The single place that rule lives.
 */
export const getAppliedThreshold = (
  filters: CategorySelection & { categoryThreshold?: number | null }
): number | null =>
  typeof filters.categoryThreshold === 'number' && hasCategorySelection(filters)
    ? filters.categoryThreshold
    : null

/**
 * `screen_impression` properties for the threshold active at page load,
 * mirroring the provider's effectiveState: flag off strips it, a category
 * with no explicit pick gets the default. Empty (omitted, not null) whenever
 * no threshold applies, so Control-A page views never carry the key.
 */
export const getThresholdPageViewProps = (
  filters: CategorySelection & { categoryThreshold?: number | null },
  enabled: boolean
): { threshold?: number } => {
  if (!enabled) return {}
  const threshold = getAppliedThreshold({
    ...filters,
    categoryThreshold: filters.categoryThreshold ?? DEFAULT_THRESHOLD,
  })
  return threshold === null ? {} : { threshold }
}
